import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, DollarSign, Key, User, Building, AlertCircle, CheckCircle, XCircle, Loader } from 'lucide-react';
import {
  useScheduledPixDetail,
  useCancelScheduledPix,
  getStatusLabel,
  getStatusColor,
  getKeyTypeLabel,
} from '../../hooks/usePixScheduler';

export function ScheduledPixDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: scheduled, isLoading, error } = useScheduledPixDetail(id || '');
  const cancelMutation = useCancelScheduledPix();
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleCancel = async () => {
    if (!id) return;
    try {
      await cancelMutation.mutateAsync({ id, reason: cancelReason || 'Cancelado pelo usuário' });
      setShowCancelModal(false);
    } catch (err) {
      console.error('Erro ao cancelar:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  const formatCurrency = (cents: number) => {
    return (cents / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const StatusIcon = ({ status }: { status: string }) => {
    switch (status) {
      case 'SCHEDULED':
        return <Calendar className="w-6 h-6 text-blue-500" />;
      case 'PROCESSING':
        return <Loader className="w-6 h-6 text-yellow-500 animate-spin" />;
      case 'COMPLETED':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'FAILED':
        return <AlertCircle className="w-6 h-6 text-red-500" />;
      case 'CANCELLED':
        return <XCircle className="w-6 h-6 text-gray-500" />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !scheduled) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Agendamento não encontrado
        </div>
      </div>
    );
  }

  const color = getStatusColor(scheduled.status);
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 border-blue-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
    gray: 'bg-gray-50 border-gray-200',
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar
      </button>

      {/* Status Card */}
      <div className={`rounded-lg border p-6 mb-6 ${colorClasses[color]}`}>
        <div className="flex items-center gap-4">
          <StatusIcon status={scheduled.status} />
          <div>
            <h2 className="text-lg font-semibold">{getStatusLabel(scheduled.status)}</h2>
            {scheduled.status === 'SCHEDULED' && (
              <p className="text-sm text-gray-600">
                Agendado para {formatDate(scheduled.scheduledDate)}
                {scheduled.scheduledTime && ` às ${scheduled.scheduledTime}`}
              </p>
            )}
            {scheduled.status === 'COMPLETED' && scheduled.processedAt && (
              <p className="text-sm text-gray-600">
                Processado em {formatDateTime(scheduled.processedAt)}
              </p>
            )}
            {scheduled.status === 'FAILED' && scheduled.errorMessage && (
              <p className="text-sm text-red-600">{scheduled.errorMessage}</p>
            )}
          </div>
        </div>
      </div>

      {/* Detalhes */}
      <div className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
        <h1 className="text-xl font-bold text-gray-900">Detalhes do Agendamento</h1>

        {/* Valor */}
        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
          <DollarSign className="w-8 h-8 text-green-500" />
          <div>
            <div className="text-sm text-gray-500">Valor</div>
            <div className="text-2xl font-bold">{formatCurrency(scheduled.amount)}</div>
          </div>
        </div>

        {/* Destino */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-900 flex items-center gap-2">
            <User className="w-4 h-4" />
            Destinatário
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-500">Chave PIX</div>
              <div className="font-medium">{scheduled.destinationKey}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Tipo de Chave</div>
              <div className="font-medium">{getKeyTypeLabel(scheduled.destinationKeyType)}</div>
            </div>
            {scheduled.destinationName && (
              <div>
                <div className="text-sm text-gray-500">Nome</div>
                <div className="font-medium">{scheduled.destinationName}</div>
              </div>
            )}
            {scheduled.destinationDocument && (
              <div>
                <div className="text-sm text-gray-500">Documento</div>
                <div className="font-medium">{scheduled.destinationDocument}</div>
              </div>
            )}
            {scheduled.destinationBank && (
              <div>
                <div className="text-sm text-gray-500">Banco</div>
                <div className="font-medium">{scheduled.destinationBank}</div>
              </div>
            )}
          </div>
        </div>

        {/* Descrição */}
        {scheduled.description && (
          <div>
            <div className="text-sm text-gray-500">Descrição</div>
            <div className="font-medium">{scheduled.description}</div>
          </div>
        )}

        {/* Informações de Processamento */}
        {(scheduled.endToEndId || scheduled.transactionId) && (
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium text-gray-900">Informações de Processamento</h3>
            <div className="grid grid-cols-2 gap-4">
              {scheduled.endToEndId && (
                <div>
                  <div className="text-sm text-gray-500">End-to-End ID</div>
                  <div className="font-mono text-sm">{scheduled.endToEndId}</div>
                </div>
              )}
              {scheduled.transactionId && (
                <div>
                  <div className="text-sm text-gray-500">ID da Transação</div>
                  <div className="font-mono text-sm">{scheduled.transactionId}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Datas */}
        <div className="space-y-4 pt-4 border-t">
          <h3 className="font-medium text-gray-900 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Histórico
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Criado em</span>
              <span>{formatDateTime(scheduled.createdAt)}</span>
            </div>
            {scheduled.notificationSentAt && (
              <div className="flex justify-between">
                <span className="text-gray-500">Notificação enviada em</span>
                <span>{formatDateTime(scheduled.notificationSentAt)}</span>
              </div>
            )}
            {scheduled.processedAt && (
              <div className="flex justify-between">
                <span className="text-gray-500">Processado em</span>
                <span>{formatDateTime(scheduled.processedAt)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Ações */}
        {scheduled.status === 'SCHEDULED' && (
          <div className="pt-4 border-t">
            <button
              onClick={() => setShowCancelModal(true)}
              className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Cancelar Agendamento
            </button>
          </div>
        )}
      </div>

      {/* Modal de Cancelamento */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4">Cancelar Agendamento</h3>
            <p className="text-gray-600 mb-4">
              Tem certeza que deseja cancelar este agendamento de PIX?
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo (opcional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                rows={3}
                placeholder="Informe o motivo do cancelamento"
              />
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
              >
                Voltar
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {cancelMutation.isPending ? 'Cancelando...' : 'Confirmar Cancelamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScheduledPixDetail;
